import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Image, FolderOpen, FileText, Mail, MailOpen, BookOpen, Star,
  UsersRound, HelpCircle, Images, Briefcase, Users, Eye,
  Wallet, CreditCard, Landmark, RefreshCw, Package, TrendingUp,
  UserPlus, FileCheck, AlertCircle, CheckCircle2, Clock, ArrowUpRight, FileSignature
} from 'lucide-react';
import api from '../../lib/api';

const PAY_STATUS = {
  pending: { label: 'Bekliyor', cls: 'bg-secondary text-muted-foreground', icon: Clock },
  waiting_approval: { label: 'Onay Bekliyor', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: Clock },
  paid: { label: 'Ödendi', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2 },
  failed: { label: 'Başarısız', cls: 'bg-destructive/10 text-destructive', icon: AlertCircle },
  rejected: { label: 'Reddedildi', cls: 'bg-destructive/10 text-destructive', icon: AlertCircle },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/auth/me'),
    ]).then(([s, u]) => {
      setStats(s.data);
      setUser(u.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Günaydın' : hour < 18 ? 'İyi günler' : 'İyi akşamlar';

  const modules = stats?.modules || {};
  const menu = modules.menu || {};
  const showMenu = (key) => menu[key] !== false;
  const num = (v) => Number(v || 0);

  // Attention items (only render those with count > 0)
  const attention = [
    num(stats?.unread_messages) > 0 && { to: '/admin/messages', icon: Mail, label: `${num(stats.unread_messages)} okunmamış mesaj`, tone: 'gold' },
    modules.payments && num(stats?.payments_pending) > 0 && { to: '/admin/payments', icon: CreditCard, label: `${num(stats.payments_pending)} ödeme onay bekliyor`, tone: 'amber' },
    modules.documents && num(stats?.docs_pending) > 0 && { to: '/admin/members', icon: FileCheck, label: `${num(stats.docs_pending)} belge incelenecek`, tone: 'amber' },
    modules.wet_signature && num(stats?.sig_pending) > 0 && { to: '/admin/members?tab=documents', icon: FileSignature, label: `${num(stats.sig_pending)} ıslak imza teslim bekliyor`, tone: 'amber' },
  ].filter(Boolean);

  // Content KPI cards filtered by menu visibility
  const kpis = [
    showMenu('projeler') && { icon: <FolderOpen />, label: 'Toplam Proje', value: num(stats?.total_projects), to: '/admin/projects' },
    showMenu('hizmetler') && { icon: <Briefcase />, label: 'Hizmetler', value: num(stats?.services), to: '/admin/services' },
    showMenu('blog') && { icon: <BookOpen />, label: 'Haberler', value: num(stats?.blog_posts), sub: `${num(stats?.published_posts)} yayında`, to: '/admin/blog' },
    { icon: <Mail />, label: 'Mesajlar', value: num(stats?.messages), badge: num(stats?.unread_messages), to: '/admin/messages' },
    { icon: <Image />, label: 'Slider', value: num(stats?.sliders), to: '/admin/sliders' },
    showMenu('ekatalog') && { icon: <FileText />, label: 'Katalog', value: num(stats?.catalogs), to: '/admin/catalogs' },
    showMenu('galeri') && { icon: <Images />, label: 'Galeri', value: num(stats?.gallery_items), to: '/admin/gallery' },
    showMenu('referanslar') && { icon: <Star />, label: 'Referanslar', value: num(stats?.testimonials), to: '/admin/testimonials' },
    showMenu('ekip') && { icon: <UsersRound />, label: 'Ekip', value: num(stats?.team_members), to: '/admin/team' },
    showMenu('sss') && { icon: <HelpCircle />, label: 'SSS', value: num(stats?.faqs), to: '/admin/faq' },
  ].filter(Boolean);

  // Quick actions filtered by module/menu
  const actions = [
    { to: '/admin/sliders', icon: <Image />, label: 'Slider Ekle' },
    showMenu('projeler') && { to: '/admin/projects', icon: <FolderOpen />, label: 'Proje Ekle' },
    showMenu('blog') && { to: '/admin/blog', icon: <BookOpen />, label: 'Haber Ekle' },
    showMenu('hizmetler') && { to: '/admin/services', icon: <Briefcase />, label: 'Hizmet Ekle' },
    showMenu('galeri') && { to: '/admin/gallery', icon: <Images />, label: 'Görsel Ekle' },
    modules.membership && { to: '/admin/members', icon: <Users />, label: 'Üyeler' },
    modules.payments && { to: '/admin/products', icon: <Package />, label: 'Ürün Ekle' },
    modules.payments && { to: '/admin/payments', icon: <CreditCard />, label: 'Ödemeler' },
  ].filter(Boolean).slice(0, 8);

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      {/* Welcome */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-[hsl(var(--gold)/0.1)] to-transparent border border-[hsl(var(--gold)/0.2)]">
        <h1 className="font-heading text-2xl font-semibold">{greeting}, {user?.name || 'Admin'}</h1>
        <p className="text-sm text-muted-foreground mt-1">Sitenizin genel durumuna buradan göz atabilirsiniz.</p>
        {attention.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="dashboard-attention">
            {attention.map((a, i) => (
              <Link key={i} to={a.to} data-testid="attention-chip"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  a.tone === 'amber'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20'
                    : 'bg-[hsl(var(--gold)/0.12)] text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold)/0.2)]'
                }`}>
                <a.icon className="w-3.5 h-3.5" /> {a.label} <ArrowUpRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Payments highlight (dynamic) */}
      {modules.payments && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="dashboard-payments-overview">
          <Link to="/admin/payments" className="lg:col-span-2 p-5 rounded-2xl bg-gradient-to-br from-[hsl(var(--gold)/0.14)] to-[hsl(var(--gold)/0.02)] border border-[hsl(var(--gold)/0.25)] hover:border-[hsl(var(--gold)/0.45)] transition-colors">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground"><Wallet className="w-4 h-4 text-[hsl(var(--gold))]" /> Toplam Gelir</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400"><TrendingUp className="w-3.5 h-3.5" /> Bu ay {fmtTL(stats?.month_revenue_tl)} ₺</span>
            </div>
            <div className="font-heading text-3xl font-bold tabular-nums mt-2">{fmtTL(stats?.revenue_tl)} <span className="text-lg">₺</span></div>
            <div className="text-[11px] text-muted-foreground mt-1">{num(stats?.payments_paid)} başarılı ödeme</div>
          </Link>
          <MiniStat icon={<RefreshCw />} label="Aktif Abonelik" value={num(stats?.subscriptions_active)} to="/admin/payments" />
          <MiniStat icon={<Package />} label="Aktif Ürün" value={num(stats?.products_active)} to="/admin/products"
            extra={num(stats?.payments_pending) > 0 ? `${num(stats.payments_pending)} onay bekliyor` : null} />
        </div>
      )}

      {/* Membership highlight (dynamic) */}
      {modules.membership && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-members-overview">
          <MiniStat icon={<Users />} label="Toplam Üye" value={num(stats?.members_total)} to="/admin/members" />
          <MiniStat icon={<CheckCircle2 />} label="Doğrulanmış Üye" value={num(stats?.members_verified)} to="/admin/members" />
          {modules.documents && <MiniStat icon={<FileCheck />} label="Bekleyen Belge" value={num(stats?.docs_pending)} to="/admin/members" tone={num(stats?.docs_pending) > 0 ? 'amber' : null} />}
          <MiniStat icon={<UserPlus />} label="Üye Kayıtları" value={num(stats?.members_total)} to="/admin/members" />
        </div>
      )}

      {/* Content KPIs */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
        </div>
      )}

      {/* Detail grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Project Breakdown */}
        {showMenu('projeler') && (
          <div className="p-5 rounded-xl bg-card border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold">Proje Dağılımı</h3>
              <Link to="/admin/projects" className="text-xs text-[hsl(var(--gold))] hover:underline">Tümü</Link>
            </div>
            <div className="space-y-3">
              <ProgressRow label="Devam Eden" value={num(stats?.ongoing_projects)} total={num(stats?.total_projects) || 1} color="bg-blue-500" />
              <ProgressRow label="Tamamlanan" value={num(stats?.completed_projects)} total={num(stats?.total_projects) || 1} color="bg-green-500" />
              <ProgressRow label="Gelecek" value={num(stats?.future_projects)} total={num(stats?.total_projects) || 1} color="bg-yellow-500" />
            </div>
          </div>
        )}

        {/* Recent Payments (dynamic) */}
        {modules.payments && (
          <div className="p-5 rounded-xl bg-card border border-border" data-testid="dashboard-recent-payments">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold">Son Ödemeler</h3>
              <Link to="/admin/payments" className="text-xs text-[hsl(var(--gold))] hover:underline">Tümü</Link>
            </div>
            {stats?.recent_payments?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_payments.slice(0, 5).map(p => {
                  const st = PAY_STATUS[p.status] || PAY_STATUS.pending;
                  return (
                    <div key={p.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-secondary/50 transition-colors">
                      {p.provider === 'wire' ? <Landmark className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <CreditCard className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{p.full_name || p.description}</div>
                        <span className={`inline-block mt-0.5 px-1.5 py-0.5 text-[9px] rounded-full ${st.cls}`}>{st.label}</span>
                      </div>
                      <span className="text-xs font-semibold text-[hsl(var(--gold))] shrink-0">{fmtTL(p.amount_tl)} ₺</span>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-xs text-muted-foreground text-center py-4">Henüz ödeme yok</p>}
          </div>
        )}

        {/* Recent Members (dynamic) */}
        {modules.membership && (
          <div className="p-5 rounded-xl bg-card border border-border" data-testid="dashboard-recent-members">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold">Son Üyeler</h3>
              <Link to="/admin/members" className="text-xs text-[hsl(var(--gold))] hover:underline">Tümü</Link>
            </div>
            {stats?.recent_members?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_members.map(m => {
                  const verified = m.email_verified === true || m.email_verified === 'true' || m.email_verified === 'True';
                  return (
                    <div key={m.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-secondary/50 transition-colors">
                      <div className="w-7 h-7 rounded-full bg-[hsl(var(--gold)/0.12)] flex items-center justify-center text-[10px] font-semibold text-[hsl(var(--gold))] shrink-0">
                        {(m.first_name?.[0] || '') + (m.last_name?.[0] || '')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{m.first_name} {m.last_name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{m.email}</div>
                      </div>
                      {verified
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        : <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-xs text-muted-foreground text-center py-4">Henüz üye yok</p>}
          </div>
        )}

        {/* Recent Messages */}
        <div className="p-5 rounded-xl bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-sm font-semibold">Son Mesajlar</h3>
            <Link to="/admin/messages" className="text-xs text-[hsl(var(--gold))] hover:underline">Tümü</Link>
          </div>
          {stats?.recent_messages?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_messages.map(m => (
                <div key={m.id} className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors">
                  <div className="mt-0.5">{m.is_read === 'true' || m.is_read === true ? <MailOpen className="w-3.5 h-3.5 text-muted-foreground" /> : <Mail className="w-3.5 h-3.5 text-[hsl(var(--gold))]" />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{m.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{m.message}</div>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(m.created_at)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-muted-foreground text-center py-4">Henüz mesaj yok</p>}
        </div>

        {/* Recent News (only if blog enabled) */}
        {showMenu('blog') && (
          <div className="p-5 rounded-xl bg-card border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold">Son Haberler</h3>
              <Link to="/admin/blog" className="text-xs text-[hsl(var(--gold))] hover:underline">Tümü</Link>
            </div>
            {stats?.recent_posts?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_posts.map(p => (
                  <div key={p.id} className="flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors">
                    <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{p.title}</div>
                    </div>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${p.is_published === 'true' || p.is_published === true ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                      {p.is_published === 'true' || p.is_published === true ? 'Yayında' : 'Taslak'}
                    </span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground text-center py-4">Henüz yazı yok</p>}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="p-5 rounded-xl bg-card border border-border">
        <h3 className="font-heading text-sm font-semibold mb-4">Hızlı İşlemler</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          {actions.map((a, i) => <QuickAction key={i} {...a} />)}
        </div>
      </div>

      {/* System Info */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 border border-border flex-wrap gap-2">
        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {num(stats?.users)} Kullanıcı</span>
          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Multi-tenant Aktif</span>
          {modules.membership && <ModuleBadge label="Üyelik" />}
          {modules.documents && <ModuleBadge label="Belge" />}
          {modules.payments && <ModuleBadge label="Ödeme" />}
        </div>
        <span className="text-[10px] text-muted-foreground">Parsek ERP v2.0</span>
      </div>
    </div>
  );
}

const KpiCard = ({ icon, label, value, badge, sub, to }) => (
  <Link to={to} className="group p-4 rounded-xl bg-card border border-border shadow-[var(--shadow-elev-1)] hover:border-[hsl(var(--gold)/0.3)] transition-colors" data-testid="dashboard-kpi-card">
    <div className="flex items-center justify-between mb-2">
      <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center">
        {React.cloneElement(icon, { className: 'w-4 h-4 text-[hsl(var(--gold))]' })}
      </div>
      {badge > 0 && <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-destructive text-destructive-foreground">{badge}</span>}
    </div>
    <div className="font-heading text-2xl font-semibold tabular-nums">{value}</div>
    <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    {sub && <div className="text-[10px] text-[hsl(var(--gold))] mt-0.5">{sub}</div>}
  </Link>
);

const MiniStat = ({ icon, label, value, to, extra, tone }) => (
  <Link to={to} className="p-5 rounded-2xl bg-card border border-border hover:border-[hsl(var(--gold)/0.3)] transition-colors" data-testid="dashboard-mini-stat">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tone === 'amber' ? 'bg-amber-500/10' : 'bg-[hsl(var(--gold)/0.1)]'}`}>
      {React.cloneElement(icon, { className: `w-4 h-4 ${tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-[hsl(var(--gold))]'}` })}
    </div>
    <div className="font-heading text-2xl font-semibold tabular-nums">{value}</div>
    <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    {extra && <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">{extra}</div>}
  </Link>
);

const ProgressRow = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const QuickAction = ({ to, icon, label }) => (
  <Link to={to} className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border hover:border-[hsl(var(--gold)/0.3)] hover:bg-[hsl(var(--gold)/0.03)] transition-colors text-center group" data-testid="dashboard-quick-action">
    <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center group-hover:bg-[hsl(var(--gold)/0.1)]">
      {React.cloneElement(icon, { className: 'w-4 h-4 text-muted-foreground group-hover:text-[hsl(var(--gold))]' })}
    </div>
    <span className="text-[11px] font-medium text-muted-foreground group-hover:text-foreground">{label}</span>
  </Link>
);

const ModuleBadge = ({ label }) => (
  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] text-[10px] font-medium">{label}</span>
);

function fmtTL(val) {
  const n = Number(val || 0);
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Az önce';
  if (diff < 3600) return `${Math.floor(diff / 60)}dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}sa`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}g`;
  return date.toLocaleDateString('tr-TR');
}
